import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { toast } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import { AuthGuard } from "./components/AuthGuard";
import { ScreenGuard } from "./components/rbac/ScreenGuard";
import { Login } from "./pages/Login";
import { BusinessesList } from "./pages/businesses/BusinessesList";
import { BusinessLocationsPage } from "./pages/locations/BusinessLocationsPage";
import { BusinessShow } from "./pages/businesses/BusinessShow";
import { BusinessCreateWizard } from "./pages/businesses/BusinessCreateWizard";
import { OwnersList } from "./pages/owners/OwnersList";
import { OwnerShow } from "./pages/owners/OwnerShow";
import { ApprovalsList } from "./pages/approvals/ApprovalsList";
import { EditRequestsList } from "./pages/editRequests/EditRequestsList";
import { CustomersList } from "./pages/customers/CustomersList";
import { BookingsList } from "./pages/bookings/BookingsList";
import { BookingShow } from "./pages/bookings/BookingShow";
import { ReviewsList } from "./pages/reviews/ReviewsList";
import { AdminAccountsList } from "./pages/adminAccounts/AdminAccountsList";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { SubscriptionsList } from "./pages/subscriptions/SubscriptionsList";
import { InvoicesList } from "./pages/invoices/InvoicesList";
import { PlanChangeRequestsList } from "./pages/planChangeRequests/PlanChangeRequestsList";
import { RevenuePage } from "./pages/revenue/RevenuePage";
import { NotificationsPage } from "./pages/notifications/NotificationsPage";
import { PlanSettingsPage } from "./pages/planSettings/PlanSettingsPage";
import { PlatformSettingsPage } from "./pages/platformSettings/PlatformSettingsPage";
import { BrandingPage } from "./pages/branding/BrandingPage";
import { FeaturedList } from "./pages/featured/FeaturedList";
import { StaffList } from "./pages/staff/StaffList";
import { MedicinesPage } from "./pages/medicines/MedicinesPage";
import { RolesList } from "./pages/staff/RolesList";
import { RoleEditor } from "./pages/staff/RoleEditor";
import { PrivacyPolicy } from "./pages/legal/PrivacyPolicy";
import { TermsAndConditions } from "./pages/legal/TermsAndConditions";
import { Toaster } from "./components/ui/sonner";
import { DebugConsole } from "./components/DebugConsole";
import { authEvents, installAuthFetchInterceptor } from "./utils/auth";
import { API_BASE_URL } from "./config/api";
import { useFcmMessages } from "./hooks/useFcmMessages";

const NotificationSetup = () => {
  useFcmMessages();
  return null;
};

const SessionExpiryHandler = () => {
  useEffect(() => {
    installAuthFetchInterceptor(API_BASE_URL);
    const onSessionExpired = () => window.location.replace("/login");
    const onForbidden = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail;
      toast.error(detail?.message || "You don't have permission to do that.");
    };
    window.addEventListener(authEvents.sessionExpired, onSessionExpired);
    window.addEventListener("rbac:forbidden", onForbidden);
    return () => {
      window.removeEventListener(authEvents.sessionExpired, onSessionExpired);
      window.removeEventListener("rbac:forbidden", onForbidden);
    };
  }, []);
  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <BrowserRouter>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light">
        <BrandingProvider>
          <AuthProvider>
            <SessionExpiryHandler />
            <NotificationSetup />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<TermsAndConditions />} />
              <Route element={<AuthGuard />}>
                <Route path="/" element={<DashboardPage />} />
                <Route
                  path="/businesses"
                  element={
                    <ScreenGuard module="businesses">
                      <BusinessesList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/locations"
                  element={
                    <ScreenGuard module="businesses">
                      <BusinessLocationsPage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/businesses/create"
                  element={
                    <ScreenGuard module="businesses">
                      <BusinessCreateWizard />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/businesses/:id"
                  element={
                    <ScreenGuard module="businesses">
                      <BusinessShow />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/providers"
                  element={<Navigate to="/businesses" replace />}
                />
                <Route
                  path="/providers/:id"
                  element={<Navigate to="/businesses" replace />}
                />
                <Route
                  path="/owners"
                  element={
                    <ScreenGuard module="owners">
                      <OwnersList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/owners/:id"
                  element={
                    <ScreenGuard module="owners">
                      <OwnerShow />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/approvals"
                  element={
                    <ScreenGuard module="approvals">
                      <ApprovalsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/edit-requests"
                  element={
                    <ScreenGuard module="businesses">
                      <EditRequestsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <ScreenGuard module="customers">
                      <CustomersList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/bookings"
                  element={
                    <ScreenGuard module="bookings">
                      <BookingsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/bookings/:id"
                  element={
                    <ScreenGuard module="bookings">
                      <BookingShow />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/subscriptions"
                  element={
                    <ScreenGuard module="subscriptions">
                      <SubscriptionsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <ScreenGuard module="subscriptions">
                      <InvoicesList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/plan-change-requests"
                  element={
                    <ScreenGuard module="subscriptions">
                      <PlanChangeRequestsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/revenue"
                  element={
                    <ScreenGuard module="revenue">
                      <RevenuePage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/featured"
                  element={
                    <ScreenGuard module="featured">
                      <FeaturedList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/reviews"
                  element={
                    <ScreenGuard module="reviews">
                      <ReviewsList />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ScreenGuard module="notifications">
                      <NotificationsPage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/plan-settings"
                  element={
                    <ScreenGuard module="planSettings">
                      <PlanSettingsPage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/admin-accounts"
                  element={
                    <ScreenGuard module="staffManagement">
                      <AdminAccountsList />
                    </ScreenGuard>
                  }
                />
                <Route path="/staff" element={<StaffList />} />
                <Route path="/roles" element={<RolesList />} />
                <Route path="/roles/new" element={<RoleEditor />} />
                <Route path="/roles/:id" element={<RoleEditor />} />
                <Route
                  path="/platform-settings"
                  element={
                    <ScreenGuard module="platformSettings">
                      <PlatformSettingsPage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/medicines"
                  element={
                    <ScreenGuard module="platformSettings">
                      <MedicinesPage />
                    </ScreenGuard>
                  }
                />
                <Route
                  path="/branding"
                  element={
                    <ScreenGuard module="branding">
                      <BrandingPage />
                    </ScreenGuard>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </AuthProvider>
          <Toaster />
          {import.meta.env.DEV && <DebugConsole />}
        </BrandingProvider>
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </BrowserRouter>
);

export default App;
